import type { ExtendedColumnDef } from "@/components/tables/columns";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { Progress } from "@/components/ui/progress";
import {
	type BillingUsageItem,
	formatFeatureName,
	getFeatureIcon,
	getUsageStatus,
	utilizationBadgeVariant,
} from "@/lib/billing-usage";
import { cn } from "@/lib/utils";

type UsageTableProps = {
	data: BillingUsageItem[];
	loading: boolean;
	className?: string;
};

export function UsageTable({ data, loading, className }: UsageTableProps) {
	const columns: ExtendedColumnDef<BillingUsageItem>[] = [
		{
			header: "Feature",
			accessorKey: "feature_key",
			cell: ({ row }) => {
				const Icon = getFeatureIcon(row.getValue("feature_key"));
				return (
					<div className="flex min-w-0 items-center gap-2">
						<Icon className="size-5 shrink-0 text-muted-foreground sm:size-4" />
						<span className="min-w-0 truncate">
							{formatFeatureName(row.getValue("feature_key"))}
						</span>
					</div>
				);
			},
		},
		{
			header: "Usage",
			accessorKey: "usage",
			cell: ({ row }) => (
				<span className="font-medium">
					{Number(row.getValue("usage")).toLocaleString()}
				</span>
			),
			enableSorting: true,
		},
		{
			header: "Limit",
			accessorKey: "limit",
			cell: ({ row }) => {
				const lim = row.getValue("limit") as number | null;
				return (
					<span className="font-medium">
						{lim == null ? "∞" : Number(lim).toLocaleString()}
					</span>
				);
			},
			enableSorting: true,
		},
		{
			header: "Utilization",
			accessorKey: "utilization",
			cell: ({ row }) => {
				const usage = row.getValue("usage") as number;
				const limit = row.getValue("limit") as number | null | undefined;

				if (limit == null || limit === 0) {
					return <Badge variant="secondary">Unlimited</Badge>;
				}

				const percentage = (usage / limit) * 100;
				const { status } = getUsageStatus(usage, limit);

				return (
					<div className="flex items-center gap-2">
						<Progress value={percentage} className="h-2 w-16" />
						<Badge variant={utilizationBadgeVariant(status)}>
							{percentage.toFixed(0)}%
						</Badge>
					</div>
				);
			},
		},
		{
			header: "Period Start",
			accessorKey: "period_start",
			cell: ({ row }) =>
				new Date(row.getValue("period_start")).toLocaleDateString(),
			enableSorting: true,
		},
		{
			header: "Period End",
			accessorKey: "period_end",
			cell: ({ row }) =>
				new Date(row.getValue("period_end")).toLocaleDateString(),
			enableSorting: true,
		},
	];

	return (
		<div className={cn(className)}>
			<div className="-mx-4 overflow-x-auto px-4 sm:-mx-6 sm:px-6">
				<div className="inline-block min-w-[720px] w-full align-middle">
					<DataTable<BillingUsageItem, unknown>
						columns={columns}
						data={data}
						usePagination={true}
						pageSize={30}
						loading={loading}
						filterName="feature"
						filterColumn={"feature_key"}
						hiddenColumns={[]}
					/>
				</div>
			</div>
		</div>
	);
}
